// Central navigation config for Tô de Olho.
// Reused by BottomNav (mobile), Sidebar (desktop), MobileDrawer and Header.
// Keep icon imports minimal and consistent.
import {
  Search, Users, User, Bell, MapPin, DollarSign, Vote, Radar,
  History, Eye, Globe, FileText, Crown, LogOut, Shield, Building2,
  Landmark, Flag,
} from 'lucide-react';

// === Mobile bottom nav — exactly 5 tabs (one-tap access to core flows) ===
// The `Perfil` tab is resolved dynamically: logged-in => /perfil, guest => /auth.
export const bottomTabs = [
  { key: 'buscar',   path: '/',                icon: Search,    label: 'Buscar',   testId: 'bottom-buscar'   },
  { key: 'cidade',   path: '/meu-municipio',   icon: MapPin,    label: 'Cidade',   testId: 'bottom-cidade'   },
  { key: 'emendas',  path: '/emendas',         icon: Landmark,  label: 'Emendas',  testId: 'bottom-emendas'  },
  { key: 'alertas',  path: '/alertas',         icon: Bell,      label: 'Alertas',  testId: 'bottom-alertas', badgeKey: 'unread' },
  // `resolvePath` handled inside BottomNav based on auth state.
  { key: 'perfil',   path: '/perfil',          icon: User,      label: 'Perfil',   testId: 'bottom-perfil', authAware: true },
];

// === Sidebar categories (desktop) & Mobile drawer (full menu) ===
// One source of truth so the two menus never drift.
export const navCategories = [
  {
    title: 'Candidatos',
    items: [
      { path: '/',         icon: Search, label: 'Buscar candidato', testId: 'nav-buscar' },
      { path: '/compare',  icon: Users,  label: 'Comparar candidatos', testId: 'nav-comparar' },
      { path: '/meu-politico', icon: Vote, label: 'Meu político', testId: 'nav-meu-politico' },
    ],
  },
  {
    title: 'Minha Cidade',
    items: [
      { path: '/meu-municipio',    icon: MapPin,    label: 'Radar do município', testId: 'nav-meu-municipio' },
      { path: '/dinheiro-publico', icon: DollarSign,label: 'Dinheiro público',   testId: 'nav-dinheiro' },
      { path: '/radar',            icon: Radar,     label: 'Mapa eleitoral',     testId: 'nav-radar' },
    ],
  },
  {
    title: 'Transparência',
    items: [
      { path: '/emendas', icon: Landmark,  label: 'Emendas parlamentares', testId: 'nav-emendas' },
      { path: '/cenario-eleitoral', icon: Vote, label: 'Cenário eleitoral', testId: 'nav-cenario-eleitoral' },
      { path: '/empresa', icon: Building2, label: 'Dossiê da empresa',     testId: 'nav-empresa' },
      { path: '/brasil-transparente', icon: Flag, label: 'Brasil Transparente', testId: 'nav-brasil-transparente' },
    ],
  },
  {
    title: 'Acompanhar',
    items: [
      { path: '/alertas',   icon: Bell,    label: 'Meus alertas',  testId: 'nav-alertas', badgeKey: 'unread' },
      { path: '/history',   icon: History, label: 'Histórico',     testId: 'nav-historico' },
    ],
  },
  {
    title: 'Mais',
    items: [
      { path: '/monitor',       icon: Globe,   label: 'Painel 360°',      testId: 'nav-monitor' },
      { path: '/fique-de-olho', icon: Eye,     label: 'Fique de olho',    testId: 'nav-fdo' },
      { path: '/transparencia', icon: FileText,label: 'Gastos & mandato', testId: 'nav-transparency' },
      { path: '/eleicoes',      icon: Vote,    label: 'Eleições',         testId: 'nav-elections' },
    ],
  },
  {
    title: 'Minha Conta',
    items: [
      { path: '/perfil', icon: User,  label: 'Perfil',   testId: 'nav-perfil', authAware: true },
      { path: '/planos', icon: Crown, label: 'Planos',   testId: 'nav-planos' },
    ],
  },
];

// === Desktop header quick links (top-right compact menu) ===
export const headerQuickLinks = [
  { path: '/meu-municipio',    label: 'Cidade',   testId: 'quick-cidade' },
  { path: '/dinheiro-publico', label: 'Dinheiro', testId: 'quick-dinheiro' },
  { path: '/compare',          label: 'Comparar', testId: 'quick-comparar' },
  { path: '/alertas',          label: 'Alertas',  testId: 'quick-alertas' },
];

// === Home feature cards — the "O que você quer descobrir?" grid ===
export const homeFeatureCards = [
  {
    path: '/brasil-transparente', icon: Flag, testId: 'card-brasil-transparente',
    title: 'Brasil Transparente',
    subtitle: 'Representantes, emendas e municípios de PE',
    tone: 'from-[#D1FAE5] to-[#ECFDF5]',
  },
  {
    path: '/emendas', icon: Landmark, testId: 'card-emendas',
    title: 'Emendas parlamentares',
    subtitle: 'Para onde vai o dinheiro do seu deputado',
    tone: 'from-[#DCFCE7] to-[#F0FDF4]',
  },
  {
    path: '/cenario-eleitoral', icon: Vote, testId: 'card-cenario-eleitoral',
    title: 'Cenário eleitoral',
    subtitle: 'Pesquisas oficiais registradas no TSE',
    tone: 'from-[#DBEAFE] to-[#EFF6FF]',
  },
  {
    path: '/meu-municipio', icon: MapPin, testId: 'card-meu-municipio',
    title: 'Minha cidade',
    subtitle: 'Prefeito, orçamento e serviços',
    tone: 'from-[#E8F5E9] to-[#F0FDF4]',
  },
  {
    path: '/empresa', icon: Building2, testId: 'card-empresa',
    title: 'Dossiê da empresa',
    subtitle: 'Quanto uma empresa recebeu',
    tone: 'from-[#E0F2FE] to-[#F0F9FF]',
  },
  {
    path: '/meu-politico', icon: Vote, testId: 'card-meu-politico',
    title: 'Meu político',
    subtitle: 'Como votou seu deputado',
    tone: 'from-[#DBEAFE] to-[#EFF6FF]',
  },
  {
    path: '/compare', icon: Users, testId: 'card-comparar',
    title: 'Comparar candidatos',
    subtitle: 'Lado a lado, sem enrolação',
    tone: 'from-[#FCE7F3] to-[#FDF2F8]',
  },
  {
    path: '/alertas', icon: Bell, testId: 'card-alertas',
    title: 'Meus alertas',
    subtitle: 'Receba avisos por e-mail',
    tone: 'from-[#FEE2E2] to-[#FEF2F2]',
  },
];

export const iconMap = { Shield, LogOut };
